/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import {
  CreateOverrideDto,
  CreateOverrideResponseDto,
  GeometryDto,
  GetSegmentInfoResponseDto,
  OverrideDto,
  OverrideListResponseDto,
  OverrideService,
  SegmentDirectionalSpeedLimitDto,
  SegmentService,
  UpdateOverrideDto
} from 'customer-speed-api';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { polylineDistanceKm } from '../../../utils';
import { ILatLng, ILatLngBounds, LatLng } from '../../core/models';
import { EDirection, EOverrideStatus, IFilters, IOverrideListResult, IPostedSpeedUpdate, IRoadDetails } from '../models';
import { ROAD_DIRECTION, RoadDirection } from '../models/road-details.interface';
import { decodeGeometry, stitchPolylines } from '../utils/decode-geometry';

@Injectable({
  providedIn: 'root'
})
export class ManagePostedSpeedService {
  constructor(
    private readonly _overrideService: OverrideService,
    private readonly _segmentService: SegmentService
  ) {}

  /**
   * Gets segment info for a map location and maps it to a road details view model.
   * Speed limit and unit come directly from the API response.
   */
  public getSegmentInfo(lat: number, lng: number): Observable<{ mapCenter: LatLng; roadDetails: IRoadDetails }> {
    return this._segmentService.v1SegmentsGet(lat, lng, true).pipe(
      map(segmentInfo => ({
        mapCenter: this._getSegmentCenter(segmentInfo, new LatLng(lat, lng)),
        roadDetails: this._mapSegmentInfoToRoadDetails(segmentInfo)
      }))
    );
  }

  /**
   * Gets segment info for a map location, returning the full API response alongside
   * the mapped road details and computed center. Used by the effects pipeline for
   * override conflict detection and geometry storage.
   */
  public getSegmentInfoFull(
    lat: number,
    lng: number
  ): Observable<{ mapCenter: LatLng; roadDetails: IRoadDetails; segmentInfo: GetSegmentInfoResponseDto }> {
    return this._segmentService.v1SegmentsGet(lat, lng, true).pipe(
      map(segmentInfo => ({
        mapCenter: this._getSegmentCenter(segmentInfo, new LatLng(lat, lng)),
        roadDetails: this._mapSegmentInfoToRoadDetails(segmentInfo),
        segmentInfo
      }))
    );
  }

  /**
   * Gets all overrides globally (without geographic bounds) and maps them to view models.
   * Passes status and date filters to the API for server-side filtering.
   */
  public getAllOverrides(filters: IFilters): Observable<IOverrideListResult> {
    const status = filters.statuses.length > 0 ? filters.statuses.join(',').toLowerCase() : undefined;
    const startDate = this._toIsoString(filters.startDate);
    const endDate = this._toIsoString(filters.endDate, true);

    return this._overrideService
      .v1OverridesGet(undefined, undefined, undefined, undefined, undefined, undefined, status, startDate, endDate)
      .pipe(map(response => this._mapOverrideListResponse(response)));
  }

  /**
   * Gets overrides within map bounds and maps them to view models.
   * Passes status and date filters to the API for server-side filtering.
   */
  public getOverrides(bounds: ILatLngBounds, filters: IFilters): Observable<IOverrideListResult> {
    const status = filters.statuses.length > 0 ? filters.statuses.join(',').toLowerCase() : undefined;
    const startDate = this._toIsoString(filters.startDate);
    const endDate = this._toIsoString(filters.endDate, true);

    return this._overrideService
      .v1OverridesGet(bounds.north, bounds.south, bounds.east, bounds.west, undefined, undefined, status, startDate, endDate)
      .pipe(map(response => this._mapOverrideListResponse(response)));
  }

  /**
   * Creates a new override
   */
  public createOverride(override: CreateOverrideDto): Observable<CreateOverrideResponseDto> {
    return this._overrideService.v1OverridesPost(override);
  }

  /**
   * Updates an existing override
   */
  public updateOverride(overrideId: string, override: UpdateOverrideDto): Observable<OverrideDto> {
    return this._overrideService.v1OverridesOverrideIdPut(overrideId, override);
  }

  /**
   * Deletes an override
   */
  public deleteOverride(overrideId: string): Observable<any> {
    return this._overrideService.v1OverridesOverrideIdDelete(overrideId);
  }

  private _mapOverrideListResponse(response: OverrideListResponseDto): IOverrideListResult {
    return {
      overrides: this._mapOverridesToPostedSpeedUpdates(response.items || []),
      totalItems: response.totalItems,
      totalAccountOverrides: response.totalAccountOverrides
    };
  }

  private _mapOverridesToPostedSpeedUpdates(overrides: OverrideDto[]): IPostedSpeedUpdate[] {
    return overrides.map(override => {
      const { geometry, segmentGeometries } = this._decodeOverrideGeometries(override);
      const distanceKm = this._calculateOverrideDistanceKm(override);
      return {
        id: override.id,
        roadName: override.roadName ?? null,
        cityName: override.cityName ?? null,
        speedLimit: this._formatSpeedLimit(override),
        speedOverwriteInKmh: override.speedOverwriteInKmh,
        direction: this._getDirection(override),
        startDate: this._getStartDate(override),
        endDate: this._getEndDate(override),
        distanceKm,
        note: override.notes ?? null,
        status: this._getStatus(override),
        geometry,
        segmentGeometries,
        segmentId: this._getSegmentId(override),
        segmentRef: this._getSegmentRef(override),
        segmentDirection: this._getOverrideSegmentDirection(override)
      };
    });
  }

  /**
   * Decodes geometry from all segments of an override DTO.
   * Returns both the merged polyline (for endpoint markers and distance)
   * and the individual segment geometries (for per-segment rendering).
   */
  private _decodeOverrideGeometries(override: OverrideDto): {
    geometry: ILatLng[] | undefined;
    segmentGeometries: ILatLng[][] | undefined;
  } {
    const segments = override.segments;
    if (!segments?.length) {
      return { geometry: undefined, segmentGeometries: undefined };
    }

    const decoded: ILatLng[][] = [];
    for (const seg of segments) {
      if (seg.geometry?.length) {
        const points = decodeGeometry(seg.geometry);
        if (points.length > 0) {
          decoded.push(points);
        }
      }
    }

    if (decoded.length === 0) {
      return { geometry: undefined, segmentGeometries: undefined };
    }

    const merged = stitchPolylines(decoded);

    if (override.direction?.toLowerCase() === 'backward') {
      merged.reverse();
      // Also reverse each individual segment geometry for consistent direction
      for (const seg of decoded) {
        seg.reverse();
      }
    }

    return { geometry: merged, segmentGeometries: decoded };
  }

  /**
   * Calculates the total distance in km across all segments of an override.
   */
  private _calculateOverrideDistanceKm(override: OverrideDto): number {
    const segments = override.segments;
    if (!segments?.length) {
      return 0;
    }

    let totalDistance = 0;
    for (const seg of segments) {
      const dist = this._calculateDistanceKm(seg.geometry ?? null);
      if (dist != null) {
        totalDistance += dist;
      }
    }

    return totalDistance;
  }

  /**
   * Extracts the HERE segment identifier from the first segment of an override DTO.
   */
  private _getSegmentId(override: OverrideDto): string | undefined {
    return override.segments?.[0]?.segmentId ?? undefined;
  }

  /**
   * Extracts the HERE segment reference from the first segment of an override DTO.
   */
  private _getSegmentRef(override: OverrideDto): string | undefined {
    return override.segments?.[0]?.segmentRef ?? undefined;
  }

  private _formatSpeedLimit(override: OverrideDto): string {
    if (override.speedOverwriteInKmh == null) {
      return '--';
    }
    return `${override.speedOverwriteInKmh} km/h`; // TODO: Use user settings to convert to the correct units
  }

  private _getDirection(override: OverrideDto): string {
    return (override.direction || EDirection.BOTH).toLowerCase();
  }

  /**
   * Derives the original segment direction from the first segment in the override.
   * Falls back to the override-level direction if no segments are available.
   */
  private _getOverrideSegmentDirection(override: OverrideDto): string {
    const firstSegment = override.segments?.[0];
    if (firstSegment?.direction) {
      return firstSegment.direction.toLowerCase();
    }
    return this._getDirection(override);
  }

  private _getStartDate(override: OverrideDto): Date {
    return override.startDate ? new Date(override.startDate) : new Date();
  }

  private _getEndDate(override: OverrideDto): Date | null {
    return override.endDate ? new Date(override.endDate) : null;
  }

  private static readonly _STATUS_MAP: Record<OverrideDto.StatusEnum, EOverrideStatus> = {
    [OverrideDto.StatusEnum.Active]: EOverrideStatus.ACTIVE,
    [OverrideDto.StatusEnum.Planned]: EOverrideStatus.PLANNED,
    [OverrideDto.StatusEnum.Expired]: EOverrideStatus.EXPIRED
  } as Record<OverrideDto.StatusEnum, EOverrideStatus>;

  private _getStatus(override: OverrideDto): EOverrideStatus {
    return ManagePostedSpeedService._STATUS_MAP[override.status];
  }

  private _mapSegmentInfoToRoadDetails(segmentInfo: GetSegmentInfoResponseDto): IRoadDetails {
    const distanceKm: number | null = this._calculateDistanceKm(segmentInfo.geometry);
    const roadInfo = segmentInfo.roadInfo;
    const limits: SegmentDirectionalSpeedLimitDto[] | null = segmentInfo.directionalSpeedLimits;
    return {
      fullAddress: roadInfo?.name ?? null,
      roadName: roadInfo?.street ?? null,
      cityName: roadInfo?.city ?? null,
      speedLimitInKmh: this._getSpeedLimitInKmh(limits),
      speedDisplay: this._getSpeedDisplay(limits),
      direction: this._getSegmentDirection(limits),
      distanceKm
    };
  }

  private _getSegmentCenter(segmentInfo: GetSegmentInfoResponseDto, fallback: LatLng): LatLng {
    if (segmentInfo.coordinates) {
      return new LatLng(segmentInfo.coordinates.lat, segmentInfo.coordinates.lng);
    }
    return fallback;
  }

  private _getSpeedLimitInKmh(limits: SegmentDirectionalSpeedLimitDto[] | null): number | null {
    if (!limits?.length) return null;
    if (limits.length > 1) {
      const first: number | null | undefined = limits[0].speedLimitInKmh;
      const second: number | null | undefined = limits[1].speedLimitInKmh;
      if (first === second) {
        return first || null;
      }
      return null;
    }
    const speed: number | null | undefined = limits[0].speedLimitInKmh;
    return speed || null;
  }

  private _getSpeedDisplay(limits: SegmentDirectionalSpeedLimitDto[] | null): string | undefined {
    if (!limits?.length || limits.length < 2) return undefined;
    const first: number | null | undefined = limits[0].speedLimitInKmh;
    const second: number | null | undefined = limits[1].speedLimitInKmh;
    if (first == null || second == null || first === second) return undefined;
    return `${first} - ${second}`;
  }

  private _getSegmentDirection(limits: SegmentDirectionalSpeedLimitDto[] | null): RoadDirection {
    if (!limits?.length) return ROAD_DIRECTION.Both;
    if (limits.length === 1) return limits[0].direction.toLowerCase() as RoadDirection;
    return ROAD_DIRECTION.Both;
  }

  private _calculateDistanceKm(geometry: GeometryDto[] | null): number | null {
    if (!geometry?.length) return null;
    const points = decodeGeometry(geometry);
    return polylineDistanceKm(points);
  }

  /**
   * Converts a Date, ISO string, or date-only string (YYYY-MM-DD) to a full
   * ISO 8601 date-time string suitable for the API (date-time format).
   * For startDate: appends T00:00:00.000Z (start of day).
   * For endDate: appends T23:59:59.999Z (end of day).
   */
  private _toIsoString(value: Date | string | null, endOfDay = false): string | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') {
      // If it's already a full ISO date-time, return as-is
      if (value.includes('T')) return value;
      // Date-only string from <input type="date"> (e.g. "2025-01-15")
      return endOfDay ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
    }
    return undefined;
  }
}
